import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatOptionalId } from './memberConstants';

const MUTE_DAY_OPTIONS = [
  { value: 1, label: '1 天' },
  { value: 3, label: '3 天' },
  { value: 7, label: '7 天' },
  { value: 30, label: '30 天' },
  { value: 0, label: '永久' },
];

function isActiveMute(imMuteUntil) {
  if (!imMuteUntil) return false;
  const until = new Date(imMuteUntil).getTime();
  return Number.isFinite(until) && until > Date.now();
}

function isPermanentMute(imMuteUntil) {
  if (!imMuteUntil) return false;
  const year = new Date(imMuteUntil).getFullYear();
  return Number.isFinite(year) && year >= 9000;
}

export default function MemberMuteModal({ open, row, onClose, onSaved }) {
  const [muteDays, setMuteDays] = useState(3);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const muted = useMemo(() => isActiveMute(row?.imMuteUntil), [row?.imMuteUntil]);
  const muteLabel = useMemo(() => {
    if (!muted) return '目前未禁言';
    if (isPermanentMute(row?.imMuteUntil)) return '目前：永久禁言';
    return `目前禁言至：${row?.imMuteUntil}`;
  }, [muted, row?.imMuteUntil]);

  useEffect(() => {
    if (open && row) {
      setMuteDays(3);
      setReason('');
      setError('');
      setSubmitting(false);
    }
  }, [open, row?.id]);

  if (!open || !row) {
    return null;
  }

  async function handleMute(event) {
    event.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await adminApi.memberImMute({
        id: row.id,
        unmute: false,
        muteDays: Number(muteDays) > 0 ? Number(muteDays) : 0,
        reason: reason.trim() || undefined,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || '禁言失敗');
    } finally {
      setSubmitting(false);
    }
  }

  async function handleUnmute() {
    setSubmitting(true);
    setError('');
    try {
      await adminApi.memberImMute({
        id: row.id,
        unmute: true,
      });
      onSaved?.();
      onClose?.();
    } catch (err) {
      setError(err.message || '解除禁言失敗');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="admin-modal-overlay" role="presentation" onClick={onClose}>
      <div className="admin-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
        <div className="admin-modal__header">
          <h3>IM 禁言 [{formatOptionalId(row.id)}]</h3>
          <button type="button" className="admin-modal__close" onClick={onClose}>
            ×
          </button>
        </div>
        <form onSubmit={handleMute}>
          <div className="admin-modal__body">
            <p className="page-desc">
              用戶：{row.username || row.nickname || formatOptionalId(row.id)}
            </p>
            <p className="page-desc">{muteLabel}</p>
            <p className="page-desc">禁言後無法發訊，仍可查看歷史對話；禁用為完全停用帳號。</p>
            <div className="form-field">
              <label htmlFor="memberMuteDays">禁言天數</label>
              <select
                id="memberMuteDays"
                value={muteDays}
                onChange={(e) => setMuteDays(Number(e.target.value))}
                disabled={submitting}
              >
                {MUTE_DAY_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor="memberMuteReason">禁言理由</label>
              <textarea
                id="memberMuteReason"
                rows={3}
                maxLength={1000}
                value={reason}
                placeholder="選填，將寫入違規歷史紀錄"
                onChange={(e) => setReason(e.target.value)}
                disabled={submitting}
              />
            </div>
            {error ? <div className="alert alert-error">{error}</div> : null}
          </div>
          <div className="admin-modal__footer">
            <button type="submit" className="btn btn-danger" disabled={submitting}>
              {submitting ? '處理中…' : '確認禁言'}
            </button>
            {muted ? (
              <button
                type="button"
                className="btn btn-success"
                onClick={handleUnmute}
                disabled={submitting}
              >
                解除禁言
              </button>
            ) : null}
            <button type="button" className="btn btn-secondary" onClick={onClose} disabled={submitting}>
              關閉
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
